import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Upload, Download, Trash2, File, FileText, Image, 
  Video, Music, Archive, AlertCircle, CheckCircle,
  HardDrive, Clock
} from 'lucide-react';

const FileShare = () => {
  const { currentUser } = useAuth();
  const [files, setFiles] = useState([]);
  const [storageUsed, setStorageUsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const STORAGE_LIMIT = 1024 * 1024 * 1024; // 1GB in bytes

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/file-share', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFiles(data.files || []);
        setStorageUsed(data.storageUsed || 0);
      } else {
        throw new Error('Failed to fetch files');
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setError('Failed to load files. The file sharing feature may not be fully set up yet.');
      // For now, show mock data
      setFiles([]);
      setStorageUsed(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);

    // Check storage limit
    if (storageUsed + totalSize > STORAGE_LIMIT) {
      setError('Upload would exceed 1GB storage limit');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const token = localStorage.getItem('token');
      const response = await fetch('/api/file-share/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setSuccess(`Successfully uploaded ${selectedFiles.length} file(s)`);
        fetchFiles(); // Refresh file list
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Upload failed. The file sharing feature may not be fully set up yet.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/file-share/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('File deleted successfully');
        fetchFiles(); // Refresh file list
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError('Failed to delete file');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
      return <Image className="w-5 h-5 text-blue-400" />;
    }
    if (['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(extension)) {
      return <Video className="w-5 h-5 text-red-400" />;
    }
    if (['mp3', 'wav', 'flac', 'aac'].includes(extension)) {
      return <Music className="w-5 h-5 text-green-400" />;
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return <Archive className="w-5 h-5 text-yellow-400" />;
    }
    if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(extension)) {
      return <FileText className="w-5 h-5 text-purple-400" />;
    }
    return <File className="w-5 h-5 text-slate-400" />;
  };

  const storagePercentage = (storageUsed / STORAGE_LIMIT) * 100;

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-700 rounded w-1/4 mb-6"></div>
          <div className="h-32 bg-slate-700 rounded mb-6"></div>
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="h-16 bg-slate-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">File Share</h1>
          <p className="text-slate-400 mt-1">Temporary file storage for {currentUser?.businessUnit?.name}</p>
        </div>
        <label className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 cursor-pointer">
          <Upload className="w-4 h-4" />
          {uploading ? 'Uploading...' : 'Upload Files'}
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            disabled={uploading}
            className="hidden"
          />
        </label>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-lg flex items-center gap-3 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-900/30 border border-green-800 rounded-lg flex items-center gap-3 text-green-400">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {/* Storage Usage */}
      <div className="bg-slate-800 p-6 rounded-lg border border-slate-700 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-slate-400" />
            <h2 className="text-xl font-semibold text-slate-100">Storage Usage</h2>
          </div>
          <span className="text-slate-300">
            {formatFileSize(storageUsed)} / {formatFileSize(STORAGE_LIMIT)}
          </span>
        </div>

        <div className="w-full bg-slate-700 rounded-full h-3 mb-2">
          <div 
            className={`h-3 rounded-full transition-all duration-300 ${
              storagePercentage > 90 ? 'bg-red-500' : 
              storagePercentage > 75 ? 'bg-yellow-500' : 'bg-blue-500'
            }`}
            style={{ width: `${Math.min(storagePercentage, 100)}%` }}
          ></div>
        </div>

        <div className="flex justify-between text-sm text-slate-400">
          <span>{storagePercentage.toFixed(1)}% used</span>
          <span>{formatFileSize(STORAGE_LIMIT - storageUsed)} remaining</span>
        </div>

        {storagePercentage > 90 && (
          <div className="mt-3 p-3 bg-red-900/30 border border-red-800 rounded text-red-400 text-sm">
            Warning: You're approaching your 1GB storage limit. Consider deleting old files.
          </div>
        )}
      </div>

      {/* Files List */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="p-6 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-slate-100">Your Files ({files.length})</h2>
        </div>

        {files.length === 0 ? (
          <div className="p-8 text-center">
            <Upload className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-300 mb-2">No files uploaded yet</h3>
            <p className="text-slate-400 mb-4">
              Upload files to share them temporarily with your team
            </p>
            <label className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 cursor-pointer">
              <Upload className="w-4 h-4" />
              Upload Your First File
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {files.map((file, index) => (
              <div key={file.id || index} className="p-4 flex items-center justify-between hover:bg-slate-700/30">
                <div className="flex items-center gap-3 flex-1">
                  {getFileIcon(file.name)}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-slate-200 font-medium truncate">{file.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span>{formatFileSize(file.size)}</span>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => window.open(file.downloadUrl, '_blank')}
                    className="p-2 text-slate-400 hover:text-blue-400 hover:bg-slate-700 rounded"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleFileDelete(file.id)}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="mt-6 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
        <p className="text-slate-400 text-sm">
          <strong>Note:</strong> Files uploaded here are temporary and specific to your business unit ({currentUser?.businessUnit?.name}). 
          Files may be automatically deleted after 30 days or when storage limit is reached.
        </p>
      </div>
    </div>
  );
};

export default FileShare;